import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import './Layout.css';
import { Search, Sun, Moon, User2, ChevronRightCircle, ChevronLeftCircle, Settings } from 'lucide-react';

interface LayoutProps {
  children: React.ReactNode;
}

// Helper to get title from path
const getTitleFromPath = (pathname: string): string => {
  if (pathname === '/' || pathname === '') return 'Dashboard'; // Home page title
  const segment = pathname.split('/')[1];
  // Capitalize first letter, replace hyphens if any
  return segment ? segment.charAt(0).toUpperCase() + segment.slice(1).replace('-', ' ') : 'Page'; 
};

const Layout = ({ children }: LayoutProps) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(isMobile);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const pageTitle = getTitleFromPath(location.pathname);

  useEffect(() => {
    document.body.classList.toggle('dark-mode', isDarkMode);
  }, [isDarkMode]);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsSidebarCollapsed(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load persisted theme preference
  useEffect(() => {
    const persisted = localStorage.getItem('darkMode');
    if (persisted !== null) {
      setIsDarkMode(persisted === 'true');
    }
  }, []);

  const handleSidebarToggle = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const toggleDarkMode = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      localStorage.setItem('darkMode', String(next));
      return next;
    });
  };

  return (
    <div className={`layout ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={handleSidebarToggle} />
      <div className="content-wrapper">
        <header className="app-header">
          <span className="brand-name" onClick={() => navigate('/')}>SuperTask</span>
          <h1 className="page-title">{pageTitle}</h1>
          <div className="header-actions">
            <button className="header-icon-button" title="Search">
              <Search size={20} />
            </button>
            <button
              className="header-icon-button"
              onClick={toggleDarkMode}
              title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {isDarkMode ? <Sun size={20} /> : <Moon size={20} />}
            </button>
            <button
              className="header-icon-button"
              onClick={handleSettingsClick}
              title="Settings"
            >
              <Settings size={20} />
            </button>
            <button
              className="header-icon-button profile-button"
              onClick={handleProfileClick}
              title="Profile"
            >
              <User2 size={20} />
            </button>
          </div>
        </header>
        <main className="main-content">{children}</main>
      </div>
    </div>
  );
};

export default Layout;